import React, { useEffect, useRef, useState } from 'react';
import './ScratchCardGold.css';
import { doc, updateDoc, increment } from 'firebase/firestore';
import { db } from '../App';
import { useFireAuth } from '../FireAuthContext';
import useScratchCanvas from '../utils/useScratchCanvas';

const totalRows = 5;
const symbolsPerRow = 3;
const goldSymbol = '💰';
const goldBarSymbol = '🪙';
const nonWinningSymbols = ['🍌', '🍒', '🍇', '🍉', '🍍', '🍓', '🍑', '🍋'];
const probability = 0.04;
const goldBarProbability = 0.0001;

const ScratchCardGold = ({ refreshUser }) => {
  const { user } = useFireAuth();
  const { initCanvasBackground, getEventCoordinates, drawScratch } = useScratchCanvas();
  const canvasRefs = useRef([]);
  const [isScratching, setIsScratching] = useState(false);
  const [elements, setElements] = useState(Array(totalRows).fill([]));
  const [revealedSymbols, setRevealedSymbols] = useState(Array(totalRows).fill([]));
  const [gameEnded, setGameEnded] = useState(false);
  const [message, setMessage] = useState('');

  const generateUniqueId = () => {
    return Math.floor(Math.random() * 9000000000000) + 1000000000000;
  };

  useEffect(() => {
    const initializeCanvas = (canvas, rowIndex) => {
      initCanvasBackground(canvas);

      const initialElements = Array.from({ length: symbolsPerRow }, (_, i) => {
        const randomValue = Math.random();
        let symbol;
        if (randomValue < goldBarProbability) {
          symbol = goldBarSymbol;
        } else if (randomValue < probability) {
          symbol = goldSymbol;
        } else {
          symbol = nonWinningSymbols[Math.floor(Math.random() * nonWinningSymbols.length)];
        }

        return {
          id: i,
          x: i * (canvas.width / symbolsPerRow),
          y: 0,
          width: canvas.width / symbolsPerRow,
          height: canvas.height,
          scratched: false,
          symbol,
        };
      });

      setElements(prevElements => {
        const newElements = [...prevElements];
        newElements[rowIndex] = initialElements;
        return newElements;
      });
    };

    canvasRefs.current.forEach((canvas, index) => {
      initializeCanvas(canvas, index);
    });
  }, [initCanvasBackground]);

  const handleScratch = (e, rowIndex) => {
    if (gameEnded) return;

    const canvas = canvasRefs.current[rowIndex];
    const { x, y } = getEventCoordinates(e, canvas);
    drawScratch(canvas, x, y);

    setElements(prevElements => {
      const newElements = [...prevElements];
      const rowElements = newElements[rowIndex];
      const updatedElements = rowElements.map(element => {
        if (!element.scratched && x > element.x && x < element.x + element.width && y > element.y && y < element.y + element.height) {
          element.scratched = true;
          setRevealedSymbols(prevRevealed => {
            const newRevealed = [...prevRevealed];
            newRevealed[rowIndex] = [...newRevealed[rowIndex], element.symbol];
            return newRevealed;
          });
        }
        return element;
      });
      newElements[rowIndex] = updatedElements;
      return newElements;
    });

    const updatedRevealedSymbols = [...revealedSymbols];
    updatedRevealedSymbols[rowIndex] = elements[rowIndex].filter(element => element.scratched).map(element => element.symbol);
    setRevealedSymbols(updatedRevealedSymbols);

    if (updatedRevealedSymbols[rowIndex].length === symbolsPerRow) {
      const allRowsRevealed = updatedRevealedSymbols.every(row => row.length === symbolsPerRow);
      if (allRowsRevealed && !gameEnded) {
        setGameEnded(true);
        setTimeout(async () => {
          try {
            const goldCount = updatedRevealedSymbols.flat().filter(symbol => symbol === goldSymbol).length;
            const goldBarCount = updatedRevealedSymbols.flat().filter(symbol => symbol === goldBarSymbol).length;
            const maxGoldNuggets = Math.min(goldCount * 2, 3);
            if (goldCount > 0 || goldBarCount > 0) {
              let msg = `You Win! You have ${maxGoldNuggets} Gold Nugget(s)`;
              if (goldBarCount > 0) {
                msg += ` and ${goldBarCount * 2} Gold Bar(s)`;
              }
              msg += '.';
              setMessage(msg);
              await awardItems(maxGoldNuggets, goldBarCount);
            } else {
              setMessage('Not a winner this time 🫤 You receive 1 Gold Ore.');
              await awardItems(0, 0, true);
            }
            await incrementGamesPlayed();
            await refreshUser();
          } catch (error) {
            console.error('Error processing scratch card result:', error);
            setMessage('Error processing result. Please try again.');
          }
        }, 500);
      }
    }
  };

  const awardItems = async (goldNuggets, goldBarCount, consolation = false) => {
    if (!user) return;

    try {
      const userDocRef = doc(db, 'users', user.uid);
      const userInventory = user.inventory || [];
      let updatedInventory = [...userInventory];

      if (consolation) {
        updatedInventory = [...updatedInventory, `${generateUniqueId()}_Gold Ore`];
      } else {
        updatedInventory = [
          ...updatedInventory,
          ...Array.from({ length: goldNuggets }, () => `${generateUniqueId()}_Gold Nugget`),
          ...Array.from({ length: goldBarCount * 2 }, () => `${generateUniqueId()}_Gold Bar`)
        ];
      }

      await updateDoc(userDocRef, {
        inventory: updatedInventory
      });
    } catch (error) {
      console.error('Error awarding items:', error);
    }
  };

  const incrementGamesPlayed = async () => {
    if (user) {
      try {
        const userDocRef = doc(db, 'users', user.uid);
        await updateDoc(userDocRef, {
          gamesPlayed: increment(1),
        });
      } catch (error) {
        console.error('Error incrementing games played:', error);
      }
    }
  };

  return (
    <div className="scratch-card">
      <div className="scratch-card-rules">
        <p>Scratch and reveal any 💰 symbol to win gold, find a 🪙 for a jackpot!</p>
      </div>
      {Array.from({ length: totalRows }).map((_, rowIndex) => (
        <div key={rowIndex} className="scratch-card-row-gold">
          <div className="scratch-card-symbols">
            {elements[rowIndex].map((element, i) => (
              <div key={i} className="scratch-card-symbol">
                {element.symbol}
              </div>
            ))}
          </div>
          <canvas
            ref={el => canvasRefs.current[rowIndex] = el}
            width={320}
            height={80}
            className={`scratch-card-canvas ${revealedSymbols[rowIndex].length === symbolsPerRow ? 'fade-out' : ''}`}
            onMouseDown={() => setIsScratching(true)}
            onMouseUp={() => setIsScratching(false)}
            onMouseMove={(e) => isScratching && handleScratch(e, rowIndex)}
            onTouchStart={() => setIsScratching(true)}
            onTouchEnd={() => setIsScratching(false)}
            onTouchMove={(e) => isScratching && handleScratch(e, rowIndex)}
          />
        </div>
      ))}
      <div className="scratch-card-info">
        <p>{message}</p>
      </div>
    </div>
  );
};

export default ScratchCardGold;
